import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useOrgProject } from '../contexts/OrgProjectContext';
import SCMWizard from '../components/SCMWizard';
import PageLayout from '../components/PageLayout';
import { apiFetch } from '../apiClient';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// ─── Design Tokens ────────────────────────────────────────────────────────────
const C = {
  navy:       '#0F172A',
  navyMid:    '#1E293B',
  navyLight:  '#334155',
  blue:       '#3B82F6',
  blueLt:     '#DBEAFE',
  green:      '#10B981',
  greenLt:    '#D1FAE5',
  white:      '#FFFFFF',
  slate:      '#64748B',
  slateLight: '#94A3B8',
  border:     '#E2E8F0',
  bg:         '#F8FAFC',
  text:       '#1E293B',
  textMid:    '#475569',
  red:        '#EF4444',
  redLt:      '#FEE2E2',
  yellow:     '#F59E0B',
  yellowLt:   '#FEF3C7',
};

function formatTimestamp(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function providerIcon(provider) {
  switch (provider) {
    case 'github': return '🐙';
    case 'gitlab': return '🦊';
    case 'azure_devops': return '🔷';
    case 'bitbucket': return '🪣';
    default: return '🔗';
  }
}

function providerLabel(provider) {
  switch (provider) {
    case 'github': return 'GitHub';
    case 'gitlab': return 'GitLab';
    case 'azure_devops': return 'Azure DevOps';
    case 'bitbucket': return 'Bitbucket';
    default: return provider || '—';
  }
}

function statusBadge(status) {
  const colors = {
    active: { bg: C.greenLt, color: C.green, label: 'Active' },
    inactive: { bg: '#F1F5F9', color: C.slate, label: 'Inactive' },
    error: { bg: C.redLt, color: C.red, label: 'Error' },
  };
  const s = colors[status] || colors.inactive;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 99,
      background: s.bg, color: s.color,
    }}>
      {s.label}
    </span>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function SCMConnections() {
  const navigate = useNavigate();
  const { orgId: routeOrgId } = useParams();
  const { orgId, orgName, syncFromRouteParams } = useOrgProject();

  // Sync context from route params on mount
  useEffect(() => {
    if (routeOrgId) {
      syncFromRouteParams(routeOrgId);
    }
  }, [routeOrgId, syncFromRouteParams]);

  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [showWizard, setShowWizard] = useState(false);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // Toast state
  const [toast, setToast] = useState(null);

  // ── Fetch connections ───────────────────────────────────────────────────────
  const fetchConnections = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setListError(null);
    try {
      const res = await apiFetch(`/api/orgs/${orgId}/scm-connections`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to load SCM connections (${res.status})`);
      }
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.data || data.results || data.items || []);
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setConnections(list);
    } catch (err) {
      setListError(err.message);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchConnections();
  }, [fetchConnections]);

  // ── Toast auto-dismiss ──────────────────────────────────────────────────────
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // ── Delete handler ──────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const res = await apiFetch(
        `/api/orgs/${orgId}/scm-connections/${deleteTarget.id}`,
        { method: 'DELETE' }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Failed to delete connection (${res.status})`);
      }
      setToast({ type: 'success', message: `Deleted "${deleteTarget.name}"` });
      setDeleteTarget(null);
      fetchConnections();
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <PageLayout>
    <div style={styles.container}>
      {/* Toast */}
      {toast && (
        <div style={{
          ...styles.toast,
          background: toast.type === 'success' ? C.green : C.red,
        }}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>SCM Connections</h1>
          <p style={styles.subtitle}>
            Manage source control connections for <strong>{orgName || 'this organisation'}</strong>
          </p>
        </div>
        <div style={styles.headerActions}>
          <button onClick={() => setShowWizard(true)} style={styles.createBtn}>
            + New Connection
          </button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div style={styles.loadingContainer}>
          <div style={styles.spinner} />
          <p style={styles.loadingText}>Loading connections…</p>
        </div>
      )}

      {/* Error */}
      {listError && (
        <div style={styles.errorBanner} role="alert">
          <span style={styles.errorIcon}>⚠</span>
          <span>{listError}</span>
          <button onClick={fetchConnections} style={styles.retryBtn}>Retry</button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !listError && connections.length === 0 && (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>🔗</div>
          <h2 style={styles.emptyTitle}>No SCM Connections</h2>
          <p style={styles.emptyText}>
            Connect a source control provider to enable repository import and Git-based project sources.
          </p>
          <button onClick={() => setShowWizard(true)} style={styles.createBtn}>
            + Create Connection
          </button>
        </div>
      )}

      {/* Connection list */}
      {!loading && !listError && connections.length > 0 && (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Provider</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>SCM Organisation</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Created</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {connections.map(conn => (
                <tr key={conn.id} style={styles.tr}>
                  <td style={styles.td}>
                    <span style={{ marginRight: 6 }}>{providerIcon(conn.provider)}</span>
                    {providerLabel(conn.provider)}
                  </td>
                  <td style={{ ...styles.td, fontWeight: 600 }}>{conn.name || '—'}</td>
                  <td style={styles.td}>{conn.scm_org || conn.org_name || '—'}</td>
                  <td style={styles.td}>{statusBadge(conn.status)}</td>
                  <td style={styles.td}>{formatTimestamp(conn.created_at)}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <button
                      onClick={() => setDeleteTarget(conn)}
                      style={styles.deleteBtn}
                      title="Delete connection"
                      aria-label={`Delete ${conn.name}`}
                    >
                      🗑
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div style={styles.modalOverlay} onClick={() => !deleting && setDeleteTarget(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Delete Connection</h3>
            <p style={styles.modalText}>
              Are you sure you want to delete <strong>{deleteTarget.name}</strong>?
              Projects using this connection will lose access to their SCM source.
            </p>
            {deleteError && (
              <div style={styles.modalError}>{deleteError}</div>
            )}
            <div style={styles.modalActions}>
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                style={styles.cancelBtn}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                style={styles.confirmDeleteBtn}
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SCM Wizard Modal */}
      <SCMWizard
        open={showWizard}
        onClose={() => setShowWizard(false)}
        onCreated={() => {
          setShowWizard(false);
          setToast({ type: 'success', message: 'SCM connection created successfully' });
          fetchConnections();
        }}
      />
    </div>
    </PageLayout>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = {
  container: {
    padding: '24px 32px',
    maxWidth: 960,
    margin: '0 auto',
    position: 'relative',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 28,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: C.text,
    margin: '0 0 4px',
  },
  subtitle: {
    fontSize: 13,
    color: C.textMid,
    margin: 0,
  },
  headerActions: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
  },
  backBtn: {
    padding: '9px 18px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    border: `1px solid ${C.border}`,
    background: C.white,
    color: C.text,
    cursor: 'pointer',
  },
  createBtn: {
    padding: '9px 18px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 700,
    border: 'none',
    background: C.blue,
    color: C.white,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(59,130,246,0.3)',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '60px 0',
  },
  spinner: {
    width: 32,
    height: 32,
    border: `3px solid ${C.border}`,
    borderTopColor: C.blue,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
  loadingText: {
    marginTop: 12,
    fontSize: 13,
    color: C.slate,
  },
  errorBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '14px 18px',
    borderRadius: 10,
    background: C.redLt,
    color: C.red,
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 20,
  },
  errorIcon: { fontSize: 16 },
  retryBtn: {
    marginLeft: 'auto',
    padding: '6px 14px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    border: `1px solid ${C.red}`,
    background: C.white,
    color: C.red,
    cursor: 'pointer',
  },
  emptyState: {
    textAlign: 'center',
    padding: '80px 20px',
  },
  emptyIcon: { fontSize: 48, marginBottom: 16 },
  emptyTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: C.text,
    margin: '0 0 8px',
  },
  emptyText: {
    fontSize: 13,
    color: C.textMid,
    margin: '0 0 24px',
    maxWidth: 400,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  tableWrapper: {
    background: C.white,
    borderRadius: 12,
    border: `1px solid ${C.border}`,
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    fontSize: 11,
    fontWeight: 700,
    color: C.slate,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: `1px solid ${C.border}`,
    background: '#F8FAFC',
  },
  tr: {
    borderBottom: `1px solid ${C.border}`,
  },
  td: {
    padding: '14px 16px',
    fontSize: 13,
    color: C.text,
  },
  deleteBtn: {
    padding: '6px 10px',
    borderRadius: 6,
    fontSize: 14,
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    opacity: 0.6,
    transition: 'opacity 0.15s',
  },
  toast: {
    position: 'fixed',
    top: 20,
    right: 20,
    padding: '12px 20px',
    borderRadius: 8,
    color: C.white,
    fontSize: 13,
    fontWeight: 600,
    zIndex: 9999,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9000,
  },
  modal: {
    background: C.white,
    borderRadius: 14,
    padding: '28px 32px',
    maxWidth: 420,
    width: '100%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: C.text,
    margin: '0 0 12px',
  },
  modalText: {
    fontSize: 13,
    color: C.textMid,
    margin: '0 0 16px',
    lineHeight: 1.5,
  },
  modalError: {
    padding: '10px 14px',
    borderRadius: 8,
    background: C.redLt,
    color: C.red,
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 16,
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
  },
  cancelBtn: {
    padding: '9px 18px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    border: `1px solid ${C.border}`,
    background: C.white,
    color: C.text,
    cursor: 'pointer',
  },
  confirmDeleteBtn: {
    padding: '9px 18px',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 700,
    border: 'none',
    background: C.red,
    color: C.white,
    cursor: 'pointer',
  },
};
